export const AgreementType = {
  CookiePolicy: 'CookiePolicy',
  PrivacyPolicy: 'PrivacyPolicy',
  TermsOfUse: 'TermsOfUse',
} as const;

export type AgreementType = (typeof AgreementType)[keyof typeof AgreementType];
export const AiDesignerChatMessageType = {
  AskModeResponse: 'AskModeResponse',
  AssistantOpening: 'AssistantOpening',
  ComponentsGenerationCanceled: 'ComponentsGenerationCanceled',
  ComponentsGenerationFailure: 'ComponentsGenerationFailure',
  ComponentsGenerationPartialFailure: 'ComponentsGenerationPartialFailure',
  ComponentsGenerationSuccess: 'ComponentsGenerationSuccess',
  PromptAnalyzerHandleQuestionResponse: 'PromptAnalyzerHandleQuestionResponse',
  PromptAnalyzerHandleRequestFeedback: 'PromptAnalyzerHandleRequestFeedback',
  PromptAnalyzerRejectResponse: 'PromptAnalyzerRejectResponse',
  UserMessage: 'UserMessage',
} as const;

export type AiDesignerChatMessageType =
  (typeof AiDesignerChatMessageType)[keyof typeof AiDesignerChatMessageType];
export const AiDesignerChatMessageUserMode = {
  /** The user is asking the AI some question or questions. */
  Ask: 'Ask',
  /** The user is asking the AI to produce a design. */
  Design: 'Design',
} as const;

export type AiDesignerChatMessageUserMode =
  (typeof AiDesignerChatMessageUserMode)[keyof typeof AiDesignerChatMessageUserMode];
export const AiGenerationStatus = {
  Failed: 'Failed',
  Generating: 'Generating',
  Succeeded: 'Succeeded',
} as const;

export type AiGenerationStatus = (typeof AiGenerationStatus)[keyof typeof AiGenerationStatus];
export const AssetType = {
  Audio: 'Audio',
  Document: 'Document',
  Image: 'Image',
  Other: 'Other',
  Video: 'Video',
} as const;

export type AssetType = (typeof AssetType)[keyof typeof AssetType];
export const AssetVariantFormat = {
  Avif: 'Avif',
  Gif: 'Gif',
  Jpeg: 'Jpeg',
  Png: 'Png',
  Webp: 'Webp',
} as const;

export type AssetVariantFormat = (typeof AssetVariantFormat)[keyof typeof AssetVariantFormat];
export const AssetVariantMode = {
  /** Convert the source variant to a different format. */
  Convert: 'Convert',
  /** Crop the source variant. */
  Crop: 'Crop',
  /** Generate a variant using AI. Can reference a source variant but does not require it. */
  Generate: 'Generate',
  /** Users manually upload the asset variant file. */
  ManualUpload: 'ManualUpload',
  /** Users manually provide a URL for the asset variant. */
  ManualUrl: 'ManualUrl',
  /** Optimize the source variant. */
  Optimize: 'Optimize',
  /** Resize the source variant to the specified width and height. */
  Resize: 'Resize',
  /** Resize and optimize the source variant. */
  ResizeAndOptimize: 'ResizeAndOptimize',
} as const;

export type AssetVariantMode = (typeof AssetVariantMode)[keyof typeof AssetVariantMode];
export const AssetVariantVersionChange = {
  Archived: 'Archived',
  FileId: 'FileId',
  Format: 'Format',
  GeneratePrompt: 'GeneratePrompt',
  HeightPx: 'HeightPx',
  ManualUrl: 'ManualUrl',
  Name: 'Name',
  Quality: 'Quality',
  SourceAssetVariantId: 'SourceAssetVariantId',
  WidthPx: 'WidthPx',
} as const;

export type AssetVariantVersionChange =
  (typeof AssetVariantVersionChange)[keyof typeof AssetVariantVersionChange];
export const AssetVersionChange = {
  Alt: 'Alt',
  Archived: 'Archived',
  Name: 'Name',
  ProductItem: 'ProductItem',
  Roles: 'Roles',
  Tags: 'Tags',
} as const;

export type AssetVersionChange = (typeof AssetVersionChange)[keyof typeof AssetVersionChange];
export const BlogPostingMarkupMode = {
  Disabled: 'Disabled',
  Enabled: 'Enabled',
  Inherit: 'Inherit',
} as const;

export type BlogPostingMarkupMode =
  (typeof BlogPostingMarkupMode)[keyof typeof BlogPostingMarkupMode];
export const ConfirmationAction = {
  /** Display only a message. */
  Message: 'Message',
  /** Display a message and keep the form as is. */
  MessageKeepForm: 'MessageKeepForm',
  /** Redirect to a URL. */
  Redirect: 'Redirect',
} as const;

export type ConfirmationAction = (typeof ConfirmationAction)[keyof typeof ConfirmationAction];
export const CourseVersionChange = {
  Archived: 'Archived',
  EmptyNotesMessage: 'EmptyNotesMessage',
  EnableNotes: 'EnableNotes',
  LessonCompletionMode: 'LessonCompletionMode',
  MaxDepth: 'MaxDepth',
  Title: 'Title',
} as const;

export type CourseVersionChange = (typeof CourseVersionChange)[keyof typeof CourseVersionChange];
export const CustomFieldDeployStage = {
  Create: 'Create',
  Live: 'Live',
  Remove: 'Remove',
} as const;

export type CustomFieldDeployStage =
  (typeof CustomFieldDeployStage)[keyof typeof CustomFieldDeployStage];
export const CustomFieldType = {
  Boolean: 'Boolean',
  CustomFieldId: 'CustomFieldId',
  /** A plain date. */
  Date: 'Date',
  /** A plain date and a plain time combined. */
  DateTime: 'DateTime',
  Float: 'Float',
  FormId: 'FormId',
  FormTemplateId: 'FormTemplateId',
  Instant: 'Instant',
  Int: 'Int',
  MixerComponents: 'MixerComponents',
  OrgId: 'OrgId',
  PageId: 'PageId',
  PageTemplateId: 'PageTemplateId',
  ProductId: 'ProductId',
  RichText: 'RichText',
  SiteId: 'SiteId',
  String: 'String',
  SubscriptionId: 'SubscriptionId',
  UserId: 'UserId',
} as const;

export type CustomFieldType = (typeof CustomFieldType)[keyof typeof CustomFieldType];
export const CustomTableObject = {
  Form: 'Form',
  FormTemplate: 'FormTemplate',
  Org: 'Org',
  Page: 'Page',
  PageTemplate: 'PageTemplate',
  Product: 'Product',
  Site: 'Site',
  Subscription: 'Subscription',
  User: 'User',
} as const;

export type CustomTableObject = (typeof CustomTableObject)[keyof typeof CustomTableObject];
export const CustomTableVersionDeployStage = {
  Backfill: 'Backfill',
  Create: 'Create',
  Publish: 'Publish',
  Validate: 'Validate',
} as const;

export type CustomTableVersionDeployStage =
  (typeof CustomTableVersionDeployStage)[keyof typeof CustomTableVersionDeployStage];
export const DateFilterOperator = {
  After: 'After',
  Before: 'Before',
  Equals: 'Equals',
  NotEquals: 'NotEquals',
} as const;

export type DateFilterOperator = (typeof DateFilterOperator)[keyof typeof DateFilterOperator];
export const DesignerObjectType = {
  Component: 'Component',
  ComponentPropsForm: 'ComponentPropsForm',
  CustomFieldsForm: 'CustomFieldsForm',
  Form: 'Form',
  FormConfirmationMessage: 'FormConfirmationMessage',
  FormStep: 'FormStep',
  FormTemplate: 'FormTemplate',
  FormTemplateConfirmationMessage: 'FormTemplateConfirmationMessage',
  FormTemplateStep: 'FormTemplateStep',
  Layout: 'Layout',
  LayoutTemplate: 'LayoutTemplate',
  Lesson: 'Lesson',
  Page: 'Page',
  PageTemplate: 'PageTemplate',
  PageTemplatePropsForm: 'PageTemplatePropsForm',
  RemoteComponentPropsForm: 'RemoteComponentPropsForm',
  Stage: 'Stage',
} as const;

export type DesignerObjectType = (typeof DesignerObjectType)[keyof typeof DesignerObjectType];
export const ExtensionVersionBuildStage = {
  Building: 'Building',
  Built: 'Built',
  Canceled: 'Canceled',
  Failed: 'Failed',
} as const;

export type ExtensionVersionBuildStage =
  (typeof ExtensionVersionBuildStage)[keyof typeof ExtensionVersionBuildStage];
export const ExtensionVersionUploadStage = {
  Canceled: 'Canceled',
  Uploaded: 'Uploaded',
  Uploading: 'Uploading',
} as const;

export type ExtensionVersionUploadStage =
  (typeof ExtensionVersionUploadStage)[keyof typeof ExtensionVersionUploadStage];
export const FileStatus = {
  Canceled: 'Canceled',
  Deleted: 'Deleted',
  Uploaded: 'Uploaded',
  Uploading: 'Uploading',
} as const;

export type FileStatus = (typeof FileStatus)[keyof typeof FileStatus];
export const FormSubmissionActionType = {
  Email: 'Email',
  IntegrationAgencyMvp: 'IntegrationAgencyMvp',
  IntegrationAgencyZoom: 'IntegrationAgencyZoom',
  IntegrationHighLevel: 'IntegrationHighLevel',
  IntegrationRicochet360: 'IntegrationRicochet360',
  Webhook: 'Webhook',
} as const;

export type FormSubmissionActionType =
  (typeof FormSubmissionActionType)[keyof typeof FormSubmissionActionType];
export const FormVersionChange = {
  Components: 'Components',
  ConfirmationAction: 'ConfirmationAction',
  ConfirmationMessageComponents: 'ConfirmationMessageComponents',
  ConfirmationRedirectUrl: 'ConfirmationRedirectUrl',
  ConfirmationRedirectUrlParameters: 'ConfirmationRedirectUrlParameters',
  EmailBodyTemplate: 'EmailBodyTemplate',
  EmailSubjectTemplate: 'EmailSubjectTemplate',
  FormTemplateId: 'FormTemplateId',
  Title: 'Title',
  UseEmailBodyTemplate: 'UseEmailBodyTemplate',
  UseEmailSubjectTemplate: 'UseEmailSubjectTemplate',
} as const;

export type FormVersionChange = (typeof FormVersionChange)[keyof typeof FormVersionChange];
export const GCloudCertificateMapEntryState = {
  Active: 'Active',
  Pending: 'Pending',
  Unknown: 'Unknown',
} as const;

export type GCloudCertificateMapEntryState =
  (typeof GCloudCertificateMapEntryState)[keyof typeof GCloudCertificateMapEntryState];
export const GCloudCertificateState = {
  Active: 'Active',
  Failed: 'Failed',
  Provisioning: 'Provisioning',
  Unknown: 'Unknown',
} as const;

export type GCloudCertificateState =
  (typeof GCloudCertificateState)[keyof typeof GCloudCertificateState];
export const HelpTicketStatus = {
  Closed: 'Closed',
  Open: 'Open',
} as const;

export type HelpTicketStatus = (typeof HelpTicketStatus)[keyof typeof HelpTicketStatus];
export const IdFilterOperator = {
  Equals: 'Equals',
  In: 'In',
  NotEquals: 'NotEquals',
  NotIn: 'NotIn',
} as const;

export type IdFilterOperator = (typeof IdFilterOperator)[keyof typeof IdFilterOperator];
export const ImageSettingSource = {
  /** The image is set with an asset variant ID. */
  AssetVariant: 'AssetVariant',
  /** The image is set with a fixed URL. */
  Url: 'Url',
} as const;

export type ImageSettingSource = (typeof ImageSettingSource)[keyof typeof ImageSettingSource];
export const InvoiceFrequency = {
  Annually: 'Annually',
  Daily: 'Daily',
  Monthly: 'Monthly',
  Quarterly: 'Quarterly',
  SemiAnnually: 'SemiAnnually',
  Weekly: 'Weekly',
} as const;

export type InvoiceFrequency = (typeof InvoiceFrequency)[keyof typeof InvoiceFrequency];
export const KeywordResearchClusteringJobStatus = {
  Clustering: 'Clustering',
  Completed: 'Completed',
  Failed: 'Failed',
  Pending: 'Pending',
} as const;

export type KeywordResearchClusteringJobStatus =
  (typeof KeywordResearchClusteringJobStatus)[keyof typeof KeywordResearchClusteringJobStatus];
export const KeywordResearchJobStatus = {
  Canceled: 'Canceled',
  Completed: 'Completed',
  Failed: 'Failed',
  FilteringKeywords: 'FilteringKeywords',
  GeneratingSeedKeywords: 'GeneratingSeedKeywords',
  Paused: 'Paused',
  Pending: 'Pending',
  ResearchingKeywords: 'ResearchingKeywords',
} as const;

export type KeywordResearchJobStatus =
  (typeof KeywordResearchJobStatus)[keyof typeof KeywordResearchJobStatus];
export const KeywordResearchKeywordManualReview = {
  Default: 'Default',
  Priority: 'Priority',
  Reject: 'Reject',
} as const;

export type KeywordResearchKeywordManualReview =
  (typeof KeywordResearchKeywordManualReview)[keyof typeof KeywordResearchKeywordManualReview];
export const KeywordResearchKeywordRelevanceScore = {
  High: 'High',
  Low: 'Low',
  Medium: 'Medium',
} as const;

export type KeywordResearchKeywordRelevanceScore =
  (typeof KeywordResearchKeywordRelevanceScore)[keyof typeof KeywordResearchKeywordRelevanceScore];
export const KeywordResearchSeedKeywordStatus = {
  Completed: 'Completed',
  Failed: 'Failed',
  Pending: 'Pending',
  Researching: 'Researching',
} as const;

export type KeywordResearchSeedKeywordStatus =
  (typeof KeywordResearchSeedKeywordStatus)[keyof typeof KeywordResearchSeedKeywordStatus];
export const KeywordResearchTopicStatus = {
  Approved: 'Approved',
  Pending: 'Pending',
  Published: 'Published',
  Rejected: 'Rejected',
} as const;

export type KeywordResearchTopicStatus =
  (typeof KeywordResearchTopicStatus)[keyof typeof KeywordResearchTopicStatus];
export const KeywordResearchTopicType = {
  Cluster: 'Cluster',
  Pillar: 'Pillar',
} as const;

export type KeywordResearchTopicType =
  (typeof KeywordResearchTopicType)[keyof typeof KeywordResearchTopicType];
/** This enum defines how the lessons of a course get completed. */
export const LessonCompletionMode = {
  /** All completable input fields (those visible to the user) must be completed. */
  AllCompletableFields: 'AllCompletableFields',
  /** A custom boolean expression is set per lesson. */
  CustomExpression: 'CustomExpression',
  /** A user must manually mark a lesson as completed. */
  Manual: 'Manual',
} as const;

export type LessonCompletionMode = (typeof LessonCompletionMode)[keyof typeof LessonCompletionMode];
export const LessonVersionChange = {
  CompletionSpec: 'CompletionSpec',
  Components: 'Components',
  EnableContent: 'EnableContent',
  LessonCompletionMode: 'LessonCompletionMode',
  Location: 'Location',
  Status: 'Status',
  Title: 'Title',
} as const;

export type LessonVersionChange = (typeof LessonVersionChange)[keyof typeof LessonVersionChange];
export const LocalBusinessDayOfWeek = {
  Friday: 'Friday',
  Monday: 'Monday',
  Saturday: 'Saturday',
  Sunday: 'Sunday',
  Thursday: 'Thursday',
  Tuesday: 'Tuesday',
  Wednesday: 'Wednesday',
} as const;

export type LocalBusinessDayOfWeek =
  (typeof LocalBusinessDayOfWeek)[keyof typeof LocalBusinessDayOfWeek];
export const OptionalIdFilterOperator = {
  Equals: 'Equals',
  Exists: 'Exists',
  In: 'In',
  NotEquals: 'NotEquals',
  NotExists: 'NotExists',
  NotIn: 'NotIn',
} as const;

export type OptionalIdFilterOperator =
  (typeof OptionalIdFilterOperator)[keyof typeof OptionalIdFilterOperator];
export const OrderDirection = {
  Asc: 'Asc',
  Desc: 'Desc',
} as const;

export type OrderDirection = (typeof OrderDirection)[keyof typeof OrderDirection];
/** A field of a page that is synced from page templates. A subset of PageVersionChange. */
export const PageTemplateSyncedField = {
  BodyStyles: 'BodyStyles',
  Components: 'Components',
  MetaDescription: 'MetaDescription',
  MetaImageAssetVariantId: 'MetaImageAssetVariantId',
  MetaImageUrl: 'MetaImageUrl',
  MetaRobots: 'MetaRobots',
  MetaTitle: 'MetaTitle',
} as const;

export type PageTemplateSyncedField =
  (typeof PageTemplateSyncedField)[keyof typeof PageTemplateSyncedField];
export const PageVersionChange = {
  BlogPostingMarkupMode: 'BlogPostingMarkupMode',
  BodyStyles: 'BodyStyles',
  Components: 'Components',
  CustomFields: 'CustomFields',
  EnableLayout: 'EnableLayout',
  /** If set, then version has the updated featuredImageSource, featuredImageAssetVariantId, or featuredImageUrl. */
  FeaturedImage: 'FeaturedImage',
  LayoutId: 'LayoutId',
  MetaDescription: 'MetaDescription',
  MetaImageAssetVariantId: 'MetaImageAssetVariantId',
  MetaImageUrl: 'MetaImageUrl',
  MetaRobots: 'MetaRobots',
  MetaTitle: 'MetaTitle',
  PageTemplateId: 'PageTemplateId',
  Path: 'Path',
  ProductItem: 'ProductItem',
  Props: 'Props',
  Public: 'Public',
  PublishedAt: 'PublishedAt',
  Roles: 'Roles',
  Status: 'Status',
  Title: 'Title',
} as const;

export type PageVersionChange = (typeof PageVersionChange)[keyof typeof PageVersionChange];
export const PasswordStatus = {
  MigratedWP: 'MigratedWP',
  None: 'None',
  Ok: 'Ok',
  Temporary: 'Temporary',
} as const;

export type PasswordStatus = (typeof PasswordStatus)[keyof typeof PasswordStatus];
export const PendingDomainStatus = {
  /** The domain's DNS records are still being checked. */
  CheckingDns: 'CheckingDns',
  /** The DNS check step has timed out. */
  CheckingDnsTimedOut: 'CheckingDnsTimedOut',
  /** The domain is verified but cannot be promoted because the domain is already in use by another site. */
  Duplicate: 'Duplicate',
  /** The domain check or TLS certificate provisioning failed. */
  Failed: 'Failed',
  /** The domain is verified but cannot be promoted because the site requires payment. */
  PaymentRequired: 'PaymentRequired',
  /** The subscription check step has timed out. */
  PaymentRequiredCheckTimedOut: 'PaymentRequiredCheckTimedOut',
  /** A TLS certificate is provisioning. */
  ProvisioningTls: 'ProvisioningTls',
  /** The TLS certificate provisioning step has timed out. */
  ProvisioningTlsTimedOut: 'ProvisioningTlsTimedOut',
} as const;

export type PendingDomainStatus = (typeof PendingDomainStatus)[keyof typeof PendingDomainStatus];
export const PromoCodeType = {
  Custom: 'Custom',
  Stripe: 'Stripe',
} as const;

export type PromoCodeType = (typeof PromoCodeType)[keyof typeof PromoCodeType];
export const PublishStatus = {
  Archived: 'Archived',
  Draft: 'Draft',
  Published: 'Published',
} as const;

export type PublishStatus = (typeof PublishStatus)[keyof typeof PublishStatus];
export const SchemaOrgEntityType = {
  Organization: 'Organization',
  Person: 'Person',
} as const;

export type SchemaOrgEntityType = (typeof SchemaOrgEntityType)[keyof typeof SchemaOrgEntityType];
export const SiteBuildServiceRequestStatus = {
  Done: 'Done',
  InProgress: 'InProgress',
  Pending: 'Pending',
} as const;

export type SiteBuildServiceRequestStatus =
  (typeof SiteBuildServiceRequestStatus)[keyof typeof SiteBuildServiceRequestStatus];
export const SiteCategory = {
  /** Share articles, news, tutorials, or helpful resources */
  BlogOrInformational: 'BlogOrInformational',
  /** Promote services, sell products, or generate leads */
  Business: 'Business',
  /** Connect members, enable discussions, or share resources */
  Community: 'Community',
  /** Announce a gathering, manage sign-ups, or share details */
  Event: 'Event',
  /** Provide official information, civic resources, or services */
  GovernmentOrPublicService: 'GovernmentOrPublicService',
  /** Advance a cause, engage supporters, or accept donations */
  NonProfitOrCharity: 'NonProfitOrCharity',
  /** Showcase projects, highlight skills, or introduce yourself */
  PortfolioOrPersonal: 'PortfolioOrPersonal',
} as const;

export type SiteCategory = (typeof SiteCategory)[keyof typeof SiteCategory];
export const SitePagesOrderField = {
  PublishedAt: 'PublishedAt',
  Title: 'Title',
} as const;

export type SitePagesOrderField = (typeof SitePagesOrderField)[keyof typeof SitePagesOrderField];
export const StageBlueprintStatus = {
  Active: 'Active',
  Deleted: 'Deleted',
} as const;

export type StageBlueprintStatus = (typeof StageBlueprintStatus)[keyof typeof StageBlueprintStatus];
export const StringFilterOperator = {
  Contains: 'Contains',
  Equals: 'Equals',
  NotContains: 'NotContains',
  NotEquals: 'NotEquals',
  StartsWith: 'StartsWith',
} as const;

export type StringFilterOperator = (typeof StringFilterOperator)[keyof typeof StringFilterOperator];
export const StringListFilterOperator = {
  Contains: 'Contains',
  ContainsAll: 'ContainsAll',
  ContainsAny: 'ContainsAny',
  Empty: 'Empty',
  NotContains: 'NotContains',
  NotContainsAll: 'NotContainsAll',
  NotContainsAny: 'NotContainsAny',
  NotEmpty: 'NotEmpty',
} as const;

export type StringListFilterOperator =
  (typeof StringListFilterOperator)[keyof typeof StringListFilterOperator];
/** The supported coupon duration types. */
export const StripeCouponDuration = {
  Forever: 'Forever',
  Once: 'Once',
  Repeating: 'Repeating',
} as const;

export type StripeCouponDuration = (typeof StripeCouponDuration)[keyof typeof StripeCouponDuration];
export const SubscriptionStatus = {
  Active: 'Active',
  Canceled: 'Canceled',
  Incomplete: 'Incomplete',
  IncompleteExpired: 'IncompleteExpired',
  PastDue: 'PastDue',
  Paused: 'Paused',
  Replaced: 'Replaced',
  Trialing: 'Trialing',
  Unpaid: 'Unpaid',
} as const;

export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];
export const SyncPageTemplateToPagesSelection = {
  All: 'All',
  PageIds: 'PageIds',
} as const;

export type SyncPageTemplateToPagesSelection =
  (typeof SyncPageTemplateToPagesSelection)[keyof typeof SyncPageTemplateToPagesSelection];
export const UserStatus = {
  Active: 'Active',
  Deactivated: 'Deactivated',
  Pending: 'Pending',
} as const;

export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];
